/* eslint-disable no-underscore-dangle */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState, useEffect, useContext, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import Image from 'next/image';
import isEmpty from 'lodash/isEmpty';
import result from 'lodash/result';
import moment from 'moment';

import { withTranslation } from 'language/i18n';
import { getObjectByLanguage } from 'language/helpers';
import { bidNow } from 'models/marketplace';
import { UserContext } from 'contexts/userContext';
import { getCurrency, getUserDeposit } from 'models/payment';
import { StatusContext } from 'contexts/statusContext';
import maskAmount from 'utils/maskAmount';

import { SET_ALL } from 'contexts/constants/userConstants';

import { Button } from 'components/common';
import Modal from 'components/modals/Modal';
import useTimerHandler from 'pages/machinery/hooks/useTimerHandler';

const CheckBidModal = ({
  t, data, setData, machine, setMarketPlaceModalVisible, setBidStatusModalData, offerPlacedResponse, setOfferPlacedResponse,
}) => {
  const { userData, dispatch: userDispatch } = useContext(UserContext);
  const { setErrorState } = useContext(StatusContext);
  const { timeLeft } = useTimerHandler({ machine });

  const [onResponse, setOnResponse] = useState(false);
  const [currency, setCurrency] = useState(0);
  const [makeBidSuccess, setMakeBidSuccess] = useState(false);
  const confirmButtonRef = useRef(null);

  const { amount } = data;

  const updateBuyingLimit = async () => {
    const { userBuyingLimit } = await getUserDeposit();
    userDispatch({ type: SET_ALL, payload: { ...userData, userBuyingLimit } });
  };

  const currencyEuroFromTl = async () => {
    const approxValue = await getCurrency(amount);
    setCurrency(approxValue);
  };

  useEffect(() => {
    currencyEuroFromTl();
  }, []);

  useEffect(() => {
    if (!isEmpty(data) && confirmButtonRef.current) {
      confirmButtonRef.current.focus();
    }
  }, [data]);

  useEffect(() => {
    if (makeBidSuccess && offerPlacedResponse?.success) {
      setOnResponse(false);
      setOfferPlacedResponse();
      setData({});
      setTimeout(() => {
        setBidStatusModalData({
          amount,
        });
      }, 500);
      updateBuyingLimit();
    }

    if (offerPlacedResponse?.error?.message) {
      setData({});
      setOnResponse(false);
      setOfferPlacedResponse();
      setBidStatusModalData({
        amount,
        error: offerPlacedResponse.error,
      });
    }
  }, [makeBidSuccess, offerPlacedResponse]);

  const makeBid = async () => {
    if (!onResponse && amount > '0') {
      try {
        setOnResponse(true);
        await bidNow(machine.id, userData?.id, amount);
        setMakeBidSuccess(true);
      } catch (error) {
        console.log('makeBidErr', error);
        if (error.status === 422) {
          setData({});
          setBidStatusModalData({
            amount,
            error,
          });
        } else {
          setTimeout(() => {
            setOnResponse(false);
            setErrorState({
              title: t('alert'),
              text: error.message,
              buttonText: t('close'),
              onClickButton: () => setErrorState({}),
            });
          }, 500);
        }
      }
    }
  };

  const handleHeadColor = useMemo(() => {
    if (timeLeft?.days > 1) {
      return 'im-bidding-head--green';
    }
    return 'im-bidding-head--red';
  });

  return (
    <Modal
      visible={!isEmpty(data)}
      setVisible={() => setData({})}
    >
      <div className="modal-content modal-transparent">
        <div className="modal-body p-0">
          {
            !isEmpty(timeLeft)
            && (
              <div className="im-box im-box--white im-box--border-radius im-box--padding-0">
                <div className={`im-bidding-head ${handleHeadColor}`}>
                  <div className="row align-items-center w-100">
                    <div className="col text-right">
                      <div className="fw-700">Açık artırmanın bitmesine</div>
                      <div className="fw-400 fs-13">{`(${moment(machine.event.endDate).format('DD MMMM, HH:mm')})`}</div>
                    </div>
                    <div className="col">
                      {
                        result(timeLeft, 'days') !== undefined && timeLeft.days > 0 && (
                          <>
                            <span className="fs-25 fw-700 ml-1 mr-1">{timeLeft.days}</span>
                            <span className="fs-15">{t('day')}</span>
                          </>
                        )
                      }
                      {
                        result(timeLeft, 'hours') !== undefined && (
                          <>
                            <span className="fs-25 fw-700 ml-1 mr-1">{timeLeft.hours}</span>
                            <span className="fs-15">{t('hr')}</span>
                          </>
                        )
                      }
                      {
                        result(timeLeft, 'minutes') !== undefined && (
                          <>
                            <span className="fs-25 fw-700 ml-1 mr-1">{timeLeft.minutes}</span>
                            <span className="fs-15">{t('min')}</span>
                          </>
                        )
                      }
                      {
                        result(timeLeft, 'seconds') !== undefined && (
                          <>
                            <span className="fs-25 fw-700 ml-1 mr-1">{timeLeft.seconds}</span>
                            <span className="fs-15">{t('sec')}</span>
                          </>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
            )
          }
          <div className="im-box im-box--white im-box--border-radius-bottom im-box--padding-sm-15">
            <div className="row">
              <div className="col">
                <div className="fs-20 fw-900 text-center">Teklifinizi Onaylayın!</div>
                <p className="fs-12 text-gray3 mt-3 text-center">
                  Verilen teklifler, teklif verenin sorumluluğundadır ve bağlayıcıdır.
                  Lütfen teklifinizi gözden geçiriniz.
                </p>
                <div className="separator separator--horizontal mt-4 mb-4" />
              </div>
            </div>
            <div className="row">
              <div className="col-15 col-sm-6">
                <div className="mb-3">
                  {
                    machine?.report.images.length > 0
                      ? (
                        <Image
                          src={machine.report.images[0].main}
                          layout="responsive"
                          width="800"
                          height="800"
                          className="img-responsive"
                        />
                      )
                      : (
                        <Image
                          src="/image/machine-detail-slider-placeholder.jpg"
                          layout="responsive"
                          width="800"
                          height="800"
                          className="img-responsive"
                        />
                      )
                  }
                </div>
                <div className="mb-3">
                  <div className="fs-15 fw-900">{getObjectByLanguage(machine.title)}</div>
                </div>
                <ul className="im-list im-list--horizontal">
                  <li>
                    <div className="im-list__value">{machine.machineId}</div>
                    <div className="im-list__label">{t('machineNo')}</div>
                  </li>
                  <li>
                    <div className="im-list__value">{`${machine?.region}, ${getObjectByLanguage(machine?.country.name)}`}</div>
                    <div className="im-list__label">{t('locationDif')}</div>
                  </li>
                </ul>
              </div>
              <div className="col-15 col-sm-9">
                <div className="row mb-3">
                  <div className="col text-center">
                    <div className="fw-900 text-gray3">Teklifiniz henüz verilmemiştir.</div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col">
                    <div className="text-center mt-4 mb-4">
                      <div className="fs-35 fw-900 mb-2">{maskAmount(amount)}</div>
                      <div className="fs-15">En Yüksek Teklif</div>
                      <div className="fs-13 text-gray3 mt-1">
                        {`(${t('approx')} ${maskAmount(currency, 'TRY')})`}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row mt-auto">
                  <div className="col-15">
                    <Button
                      ref={confirmButtonRef}
                      className="im-button im-button--primary im-button--size-60 im-button--center im-button--fluid fw-900"
                      label="Teklifinizi Onaylayın"
                      withoutFormItem
                      onClick={() => makeBid()}
                      loading={onResponse}
                    />
                  </div>
                  <div className="col-15">
                    <a
                      className="im-button im-button--primary im-button--size-60 im-button--center im-button--fluid im-button--outline-yellow2 fw-900 mt-2"
                      onClick={() => {
                        setData({});
                        setMarketPlaceModalVisible(true);
                      }}
                    >
                      Teklifinizi Artırın
                    </a>
                  </div>
                  <div className="col-15 mt-3 text-center">
                    <span className="fs-12 text-gray3">
                      Makinaya en yüksek teklifi verdiğiniz takdirde, satış hakkını kazanacaksınız.
                      Teklif vererek, bu sorumluluğu kabul etmiş oluyorsunuz.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row mt-4 mb-4">
            <div className="col">
              <div className="separator separator--horizontal" />
            </div>
          </div>
          <div className="row">
            <div className="col-auto">
              <i className="fa-solid fa-info-circle text-white fs-22" />
            </div>
            <div className="col">
              <p className="text-white fs-12 fw-700">
                Verilen teklifi geri almak veya caymak mümkün değildir. Teklifiniz, açık artırmayı
                kazanan teklif olduğu takdirde makinayı satın almayı kabul etmiş olursunuz.
              </p>
              <p className="fs-12 text-gray2">
                Açık artırmasını kazandığınız iş makinasını satın almaktan vazgeçerseniz teminat
                bedeli iade alma hakkını kaybedersiniz.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

CheckBidModal.propTypes = {
  t: PropTypes.func,
  data: PropTypes.object,
  setData: PropTypes.func,
  setBidStatusModalData: PropTypes.func,
  machine: PropTypes.object,
  setMarketPlaceModalVisible: PropTypes.func,
  amount: PropTypes.string,
  offerPlacedResponse: PropTypes.object,
  setOfferPlacedResponse: PropTypes.func,
};

export default withTranslation('common')(CheckBidModal);
